import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PricingSection } from '../components/PricingSection';
import { useAuth } from '../AuthProvider';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useTrial } from '../hooks/useTrial';
import { AlertCircle, Loader2, CheckCircle, Crown, Clock } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PricingAuth: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { subscription, loading: planLoading } = usePlanLimits();
  const { trialStatus, needsPayment, isTrialActive } = useTrial();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePlanSelect = async (planId: string) => {
    if (!session) {
      // Not logged in - redirect to login
      sessionStorage.setItem('selectedPlan', planId);
      const isAnnual = sessionStorage.getItem('pricingBillingPeriod') === 'annual';
      sessionStorage.setItem('selectedBillingPeriod', isAnnual ? 'annual' : 'monthly');
      navigate('/qr/login');
      return;
    }

    // User is logged in - create Stripe checkout session
    setLoading(true);
    setError(null);

    try {
      const billingPeriod = sessionStorage.getItem('pricingBillingPeriod') === 'annual' ? 'annual' : 'monthly';
      
      console.log('🛒 Creating checkout session:', {
        planId,
        billingPeriod,
        userId: session.user?.id
      });

      const response = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          price_id: planId,
          billing_period: billingPeriod
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Checkout error:', data);
        throw new Error(data.error || 'Fehler beim Erstellen der Checkout-Session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        console.log('✅ Redirecting to Stripe Checkout:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error('Keine Checkout-URL erhalten');
      }
    } catch (err: any) {
      console.error('❌ Checkout error:', err);
      setError(err.message || 'Ein Fehler ist aufgetreten');
      setLoading(false);
    }
  };

  const getPlanDisplayName = (plan: string) => {
    const names: Record<string, string> = {
      free: 'Kostenlos',
      starter: 'Starter', 
      growth: 'Growth',
      business: 'Business'
    };
    return names[plan] || plan;
  };

  if (planLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin mr-3" />
          <span>Lade Abonnement-Informationen...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
      {/* Background grid pattern */}
      <div className="absolute inset-0 -z-10">
        <div className="h-full w-full bg-[linear-gradient(to_right,#8080801a_1px,transparent_1px),linear-gradient(to_bottom,#8080801a_1px,transparent_1px)] bg-[size:35px_35px] opacity-30 [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      </div>

      {/* Header */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {needsPayment ? 'Wählen Sie Ihren Plan' : isTrialActive ? 'Plan wählen' : 'Plan upgraden'}
            </h1>
            <p className="text-xl text-gray-600">
              {needsPayment 
                ? 'Ihre Testphase ist abgelaufen. Wählen Sie einen Plan, um fortzufahren.'
                : isTrialActive 
                  ? 'Sie befinden sich bereits in der kostenlosen Testphase. Wählen Sie einen Plan für nach dem Testzeitraum.'
                  : 'Wählen Sie den perfekten Plan für Ihre Bedürfnisse'
              }
            </p>
          </div>

          {/* Trial Active Notice */}
          {isTrialActive && trialStatus?.trial && !needsPayment && (
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center">
                <Clock className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-1">
                    Aktive Testphase
                  </h3>
                  <p className="text-blue-800">
                    Sie nutzen aktuell die kostenlose Testphase. Diese endet am{' '}
                    {trialStatus.trial.endsAt 
                      ? new Date(trialStatus.trial.endsAt).toLocaleDateString('de-DE')
                      : 'in Kürze'
                    } ({trialStatus.trial.daysLeft} Tage verbleibend).
                    Wählen Sie jetzt einen Plan, um nach der Testphase nahtlos fortzufahren.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Trial Expired Notice */}
          {needsPayment && trialStatus?.trial && (
            <div className="mb-8 bg-orange-50 border border-orange-200 rounded-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center">
                <Clock className="h-6 w-6 text-orange-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-900 mb-1">
                    Testphase abgelaufen
                  </h3>
                  <p className="text-orange-800">
                    Ihre kostenlose Testphase endete am{' '}
                    {trialStatus.trial.endsAt 
                      ? new Date(trialStatus.trial.endsAt).toLocaleDateString('de-DE')
                      : 'vor kurzem'
                    }. Wählen Sie einen Plan, um alle Features weiterhin zu nutzen.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Plan Info */}
          {subscription && subscription.plan !== 'free' && !needsPayment && (
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-1">
                    Aktueller Plan: {getPlanDisplayName(subscription.plan)}
                  </h3>
                  <p className="text-blue-800">
                    Sie haben bereits einen aktiven Plan. Nutzen Sie "Zahlungen verwalten" in Ihrem Profil, 
                    um Änderungen vorzunehmen oder den Plan zu wechseln.
                  </p>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => navigate('/qr/dash/profile?tab=subscription')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Zahlungen verwalten
                    </button>
                    <button
                      onClick={() => navigate('/qr/dash')}
                      className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Zum Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl mx-auto" data-testid="pricing-error">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-1">Fehler</h3>
                  <p className="text-red-800">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                    data-testid="pricing-error-retry"
                  >
                    Versuchen Sie es erneut
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading Overlay */}
          {loading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="pricing-loading">
              <div className="bg-white rounded-lg p-8 flex items-center shadow-xl">
                <Loader2 className="h-8 w-8 animate-spin mr-4 text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Weiterleitung zu Stripe...
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Sie werden in wenigen Sekunden weitergeleitet
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Show pricing for free users, trial expired users, or when no active subscription */}
          {(!subscription || subscription.plan === 'free' || needsPayment) && (
            <>
              <PricingSection 
                onPlanSelect={handlePlanSelect} 
                showEnterprise={true} 
                trialExpired={needsPayment || isTrialActive}
              />
              
              {/* Back to Dashboard option for trial expired users */}
              {needsPayment && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => navigate('/qr/dash')}
                    className="text-gray-600 hover:text-gray-800 underline text-sm"
                  >
                    Zurück zum Dashboard (eingeschränkte Funktionen)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Häufig gestellte Fragen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                Wie funktioniert die kostenlose Testphase?
              </h4>
              <p className="text-gray-600">
                Sie erhalten 7 Tage kostenlosen Zugang zu allen Features Ihres gewählten Plans. 
                Nach der Testphase beginnt die reguläre Abrechnung.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                Kann ich jederzeit kündigen?
              </h4>
              <p className="text-gray-600">
                Ja, Sie können Ihr Abonnement jederzeit über das Zahlungsportal kündigen. 
                Der Zugang bleibt bis zum Ende der Abrechnungsperiode bestehen.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                Welche Zahlungsmethoden akzeptieren Sie?
              </h4>
              <p className="text-gray-600">
                Wir akzeptieren alle gängigen Kreditkarten, SEPA-Lastschrift 
                und PayPal über unseren sicheren Zahlungspartner Stripe.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">
                Was passiert mit meinen Daten bei der Kündigung?
              </h4>
              <p className="text-gray-600">
                Ihre QR-Codes und Daten bleiben erhalten. Sie können jederzeit 
                wieder upgraden und haben sofort Zugang zu allen Ihren Inhalten.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingAuth; 
